import { ChevronDown } from "lucide-react";
import { useState, type ReactNode } from "react";

export interface InfoRow {
  label: string;
  value: ReactNode;
}

interface InfoCardProps {
  title: string;
  rows?: InfoRow[];
  children?: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  className?: string;
}

export default function InfoCard({
  title,
  rows,
  children,
  collapsible = false,
  defaultOpen = true,
  className = "",
}: InfoCardProps) {
  const [isOpen, setIsOpen] = useState(collapsible ? defaultOpen : true);

  return (
    <div className={`rounded-[10px] border border-line bg-white p-5 shadow-card transition-all ${className}`}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex w-full items-center justify-between text-left group cursor-pointer focus:outline-none"
          aria-expanded={isOpen}
        >
          <h3 className="text-lg font-semibold text-ink group-hover:text-primary-600 transition-colors">
            {title}
          </h3>
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-50 text-ink-secondary group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </div>
        </button>
      ) : (
        <h3 className="text-lg font-semibold text-ink">{title}</h3>
      )}

      {isOpen && (
        <div className={collapsible ? "mt-4 border-t border-[#F1F5F9] pt-1" : ""}>
          {rows && rows.length > 0 && (
            <dl className="mt-2 flex flex-col">
              {rows.map((row, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between gap-4 py-2.5 ${
                    i > 0 ? "border-t border-[#F1F5F9]" : ""
                  }`}
                >
                  <dt className="shrink-0 text-[13px] text-ink-secondary">{row.label}</dt>
                  <dd className="min-w-0 text-right text-sm font-medium text-ink">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {children}
        </div>
      )}
    </div>
  );
}